import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiMessageSquare, FiStar, FiCheckCircle, FiPlay, FiAward, FiUsers } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';
import HeroSection from '../components/HeroSection';
import dataService from '../services/dataService';

const TestimonialsPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  const iframeRefs = useRef({});

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        const data = await dataService.getTestimonials();
        setTestimonials(data);
      } catch (error) {
        console.error('Failed to load testimonials:', error);
        setTestimonials([]);
      } finally {
        setLoading(false);
      }
    };

    loadTestimonials();
  }, []);

  if (loading) {
    return (
      <div className="bg-slate-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading testimonials...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <SEO
        pageType="testimonials"
        title="Patient Testimonials | Real Success Stories | JIVA NADI Therapy Center"
        description="Read authentic patient testimonials and success stories. Real experiences from individuals who found relief through natural healing treatments."
        keywords="patient testimonials, success stories, healing experiences, chiropractic testimonials, pain relief reviews, patient feedback"
      />

      {/* Reusable Hero Section */}
      <HeroSection
        title="Trusted by 30,000+ Patients"
        subtitle="Real Patient Testimonials"
        description="Authentic healing stories from real patients. From chronic pain to complete recovery - discover how natural treatment transformed their lives."
        image="https://plus.unsplash.com/premium_photo-1739254732011-cc00c0b76e69?w=800&h=600&fit=crop"
        badge="Patient Testimonials"
        primaryButton={{
          text: "Read Stories",
          href: "#testimonials"
        }}
        secondaryButton={{
          text: "Book Consultation",
          href: "/contact"
        }}
        trustIndicators={[
          { value: "30K+", label: "Happy Patients" },
          { value: "4.9/5", label: "Average Rating" },
          { value: "95%", label: "Success Rate" }
        ]}
      />

      {/* Featured Video Testimonial */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl overflow-hidden shadow-2xl border border-gray-100"
          >
            <div className="flex flex-col lg:flex-row">
              {/* Video */}
              <div className="w-full lg:w-1/2">
                <div className="aspect-video bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
                  <iframe
                    ref={el => iframeRefs.current[testimonials[0]?.youtubeId] = el}
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${testimonials[0]?.youtubeId || ''}?enablejsapi=1`}
                    title="Featured Testimonial"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>

              {/* Content */}
              <div className="w-full lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-6 w-fit">
                  <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                  <span>Featured Success Story</span>
                </div>

                <div className="flex text-amber-400 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-6 h-6 fill-current" />
                  ))}
                </div>

                <blockquote className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 leading-tight">
                  "I was scheduled for surgery, but Dr. Saurabh fixed my L4-L5 disc bulge in just 10 sessions."
                </blockquote>

                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <span className="text-emerald-700 font-bold text-lg">RM</span>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Rajesh M.</p>
                    <p className="text-gray-600 text-sm">Mumbai • Verified Patient</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-semibold">
                    <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                    100% Non-Surgical
                  </span>
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-semibold">
                    <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                    Complete Recovery
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* More Testimonials Grid */}
      <section id="testimonials" className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-6">
              <SafeIcon icon={FiMessageSquare} className="w-4 h-4" />
              <span>More Success Stories</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
              Hear What Our Patients Say
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Real testimonials from real patients who experienced life-changing results
            </p>
          </motion.div>

          {/* Testimonials Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(1).map((video, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-2xl hover:border-emerald-200 transition-all duration-500">
                  {/* Video */}
                  <div className="aspect-video bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
                    <iframe
                      ref={el => iframeRefs.current[video.youtubeId] = el}
                      className="w-full h-full"
                      src={`https://www.youtube.com/embed/${video.youtubeId}?enablejsapi=1`}
                      title={video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex text-amber-400 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <SafeIcon key={i} icon={FiStar} className="w-4 h-4 fill-current" />
                      ))}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-emerald-600 transition-colors">
                      {video.title}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-500" />
                      <span>Verified Patient</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-20 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700 rounded-3xl p-8 lg:p-12 text-center"
          >
            <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Your Success Story Awaits
            </h3>
            <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
              Join thousands who found relief through natural healing. Start your journey today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-white text-emerald-700 px-8 py-4 rounded-full font-bold hover:bg-emerald-50 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Free Consultation
              </a>
              <a
                href="/videos"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-emerald-700 transition-all duration-300"
              >
                Watch More Videos
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TestimonialsPage;